import type { ModuleId } from "../auth/config.ts";
import type { TranslationKey } from "../ui/i18n.ts";

/**
 * Every module the exporter offers, and whether it is actually built yet.
 *
 * The full list is shown from the start, with unbuilt ones visibly
 * unavailable, rather than appearing one at a time as they land. Someone
 * deciding whether this tool covers their data needs to see the whole scope
 * up front — and an unavailable-but-listed module is honest in a way that a
 * silently absent one is not.
 *
 * `available` is the single switch that turns a module on: flipping it exposes
 * the checkbox, which feeds selection, which feeds the consent scope union.
 */
export interface ModuleDefinition {
    id: ModuleId;
    labelKey: TranslationKey;
    descriptionKey: TranslationKey;
    /** Plain-language statement of what this module's scopes allow. */
    permissionKey: TranslationKey;
    /** False until the module's export code exists (ARCHITECTURE.md §15 order). */
    available: boolean;
}

export const MODULES: readonly ModuleDefinition[] = [
    {
        id: "teams",
        labelKey: "module.teams.label",
        descriptionKey: "module.teams.description",
        permissionKey: "module.teams.permission",
        available: true
    },
    {
        id: "mail",
        labelKey: "module.mail.label",
        descriptionKey: "module.mail.description",
        permissionKey: "module.mail.permission",
        available: true
    },
    {
        id: "onedrive",
        labelKey: "module.onedrive.label",
        descriptionKey: "module.onedrive.description",
        permissionKey: "module.onedrive.permission",
        available: true
    },
    {
        id: "calendar",
        labelKey: "module.calendar.label",
        descriptionKey: "module.calendar.description",
        permissionKey: "module.calendar.permission",
        available: true
    },
    {
        id: "contacts",
        labelKey: "module.contacts.label",
        descriptionKey: "module.contacts.description",
        permissionKey: "module.contacts.permission",
        available: true
    },
    {
        id: "tasks",
        labelKey: "module.tasks.label",
        descriptionKey: "module.tasks.description",
        permissionKey: "module.tasks.permission",
        available: true
    },
    {
        id: "onenote",
        labelKey: "module.onenote.label",
        descriptionKey: "module.onenote.description",
        permissionKey: "module.onenote.permission",
        available: true
    }
];

export function moduleById(id: ModuleId): ModuleDefinition | undefined {
    return MODULES.find((m) => m.id === id);
}
